import React, { useState, useEffect, useCallback } from 'react'
import { message, Table, Button } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import DateGroupPicker from '../../../components/UI/dateGroupPicker'
import TodayTag from '../../../components/UI/todayTag'
import PageLoading from '../../../components/UI/status/loading'
import { getThisWeekRange } from '../../../utils/dateHandles'
import { getBusinessAnalysis, sendBusinessReportEmail } from '../../../api/businessReport'
import './index.sass'

const Container = styled.div`
  .businessOverview {
    padding: 24px;
  }
`

const dateRanges = [
  {
    name: 'Week',
    barWidth: 20,
    picker: 'week',
    formatX: 'ddd',
    formatTooltip: 'ddd D'
  },
  {
    name: 'Month',
    barWidth: 8,
    picker: 'month',
    formatX: 'MMM/D'
  },
  {
    name: 'Year',
    barWidth: 20,
    type: 'annual',
    picker: 'year',
    formatX: 'MMM'
  },
  {
    name: 'Custom',
    picker: 'date',
    type: 'custom'
  }
]

export default function BusinessOverview(props) {
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState(null)
  const [periods, setPeriods] = useState([])
  const [showBreakdown, setShowBreakdown] = useState(false)
  const [state, setState] = useState({
    from: getThisWeekRange().startDate.format('YYYY-MM-DD'),
    to: getThisWeekRange().endDate.format('YYYY-MM-DD')
  })

  const { from, to } = state

  useEffect(() => {
    const initial = async () => {
      const { startDate, endDate } = getThisWeekRange()
      fetchData(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'))
    }
    initial()
  }, [])

  const fetchData = useCallback(async (fromDate, toDate, dateType = null) => {
    setLoading(true)
    try {
      const params = {
        startDate: fromDate,
        endDate: toDate
      }

      const result = await getBusinessAnalysis(params)
      setSummary(result.summary || null)
      setPeriods(result.periods || [])

      // Show breakdown for Year and Custom date ranges
      const shouldShowBreakdown = dateType === 'annual' || dateType === 'year' || dateType === 'custom'
      setShowBreakdown(shouldShowBreakdown)
    } catch (error) {
      console.error('Error fetching Business Analysis:', error)
      message.error('Failed to fetch business analysis data')
      setSummary(null)
      setPeriods([])
      setShowBreakdown(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleDateFilter = useCallback(
    (fromDate, toDate, dateType = null) => {
      setState({ from: fromDate, to: toDate })
      fetchData(fromDate, toDate, dateType)
    },
    [fetchData],
  )

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0'
    return num.toLocaleString()
  }

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '$0'
    return '$' + num.toLocaleString()
  }

  const handleSendEmail = async () => {
    try {
      setLoading(true)
      await sendBusinessReportEmail({
        startDate: from,
        endDate: to
      })
      message.success('Business report sent successfully')
    } catch (error) {
      console.error('Error sending business report email:', error)
      message.error('Failed to send business report email')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      fixed: 'left',
      width: 140,
      className: 'period-column',
      align: 'center'
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'center',
      width: 130,
      render: (text) => (
        <span className="revenue-value">{formatCurrency(text)}</span>
      )
    },
    {
      title: 'New Revenue',
      dataIndex: 'newRevenue',
      key: 'newRevenue',
      align: 'center',
      width: 120,
      render: (text) => (
        <span className="new-revenue-value">{formatCurrency(text)}</span>
      )
    },
    {
      title: 'Renewal Revenue',
      dataIndex: 'renewalRevenue',
      key: 'renewalRevenue',
      align: 'center',
      width: 130,
      render: (text) => (
        <span className="renewal-revenue-value">{formatCurrency(text)}</span>
      )
    },
    {
      title: 'New Clients',
      dataIndex: 'newCount',
      key: 'newCount',
      align: 'center',
      width: 100,
      render: (text) => formatNumber(text)
    },
    {
      title: 'Renewals',
      dataIndex: 'renewalCount',
      key: 'renewalCount',
      align: 'center',
      width: 100,
      render: (text) => formatNumber(text)
    },
    {
      title: 'Total Sessions Used',
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      align: 'center',
      width: 120,
      render: (text) => formatNumber(text)
    }
  ]

  return (
    <Container>
      <div className="businessOverview">
        <div className="page-title">Business Overview</div>

        <div className="top-opr">
          <DateGroupPicker
            dateRanges={dateRanges}
            fetchData={handleDateFilter}
            defaultSelectedDate={0}
            from={from}
            to={to}
          />
          <TodayTag />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendEmail}
            disabled={loading}
            style={{ marginLeft: '16px' }}
          >
            Send Report Email
          </Button>
        </div>

        {summary && (
          <div className="stats-panel">
            <div className="stat-card">
              <div className="stat-label">Total Revenue</div>
              <div className="stat-value">{formatCurrency(summary.totalRevenue)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#52c41a' }}>
              <div className="stat-label">New Revenue</div>
              <div className="stat-value">{formatCurrency(summary.newRevenue)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
              <div className="stat-label">Renewal Revenue</div>
              <div className="stat-value">{formatCurrency(summary.renewalRevenue)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#faad14' }}>
              <div className="stat-label">New Clients</div>
              <div className="stat-value">{formatNumber(summary.newCount)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#722ed1' }}>
              <div className="stat-label">Renewals</div>
              <div className="stat-value">{formatNumber(summary.renewalCount)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#13c2c2' }}>
              <div className="stat-label">Total Invoices</div>
              <div className="stat-value">{formatNumber(summary.totalInvoices)}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#a5a3a3ff' }}>
              <div className="stat-label">Total Sessions Used</div>
              <div className="stat-value">{formatNumber(summary.totalSessions)}</div>
            </div>
          </div>
        )}

        {showBreakdown && periods && periods.length > 0 && (
          <div className="period-breakdown">
            <div className="table-container">
              <Table
                columns={columns}
                dataSource={periods}
                rowKey="periodKey"
                pagination={false}
                size="middle"
                scroll={{ x: 900 }}
                locale={{ emptyText: 'No data available' }}
              />
            </div>
          </div>
        )}

        {loading && (
          <>
            <div className="ant-modal-mask" />
            <div
              className="ant-modal-wrap"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <PageLoading />
            </div>
          </>
        )}

        {!loading && !summary && (
          <div
            style={{ textAlign: 'center', padding: '48px', color: '#8c8c8c' }}
          >
            No business data found for the selected date range.
          </div>
        )}
      </div>
    </Container>
  )
}
